import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import userService from '../services/userService';
import {
  buildPagePath,
  getSessionId,
  markLoginLoggedThisSession,
  wasLoginLoggedThisSession
} from '../utils/engagementTracking';

/**
 * Records login (once per browser session), page visits, and time on each page
 * into thrive_login_log via logLoginLog API.
 */
function UserEngagementTracker() {
  const location = useLocation();
  const pageEnteredAtRef = useRef(Date.now());
  const currentPathRef = useRef(buildPagePath(location.pathname, location.search));
  const flushingRef = useRef(false);

  const logEvent = useCallback(async (payload) => {
    try {
      await userService.logLoginLog(payload);
    } catch (err) {
      console.error('Failed to log user engagement:', err);
    }
  }, []);

  const flushCurrentPage = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      const path = currentPathRef.current;
      if (!path) return;
      const durationSeconds = Math.max(
        0,
        Math.round((Date.now() - pageEnteredAtRef.current) / 1000)
      );
      await logEvent({
        eventType: 'page_view',
        sessionId: getSessionId(),
        pagePath: path,
        durationSeconds
      });
    } finally {
      flushingRef.current = false;
    }
  }, [logEvent]);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: currentPathRef.current });
  }, []);

  useEffect(() => {
    if (wasLoginLoggedThisSession()) return;
    (async () => {
      await logEvent({
        eventType: 'login',
        sessionId: getSessionId()
      });
      markLoginLoggedThisSession();
    })();
  }, [logEvent]);

  useEffect(() => {
    const nextPath = buildPagePath(location.pathname, location.search);
    const prevPath = currentPathRef.current;

    if (prevPath !== nextPath) {
      flushCurrentPage().then(() => {
        currentPathRef.current = nextPath;
        pageEnteredAtRef.current = Date.now();
      });
      ReactGA.send({ hitType: 'pageview', page: nextPath });
    }
  }, [location.pathname, location.search, flushCurrentPage]);

  useEffect(() => {
    const onPageHide = () => {
      flushCurrentPage();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        onPageHide();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flushCurrentPage();
    };
  }, [flushCurrentPage]);

  return null;
}

export default UserEngagementTracker;
